import * as React from "react";
import { SingleNavMenuLink } from "../../../pages/identity/useNavMenu";
import { nextTick } from "../../../services/utils";

export type SingleNavMenuContextType = {
  activeLinkId: string;
  setActiveLinkId: React.Dispatch<React.SetStateAction<string>>;
  scrollToSection: (linkId: string) => void;
};

export const SingleNavMenuContext = React.createContext<SingleNavMenuContextType>(null as any);

export function useInitSingleNavMenuContext(links: SingleNavMenuLink[], pagesWithScrolls: string[]) {
  const [activeLinkId, setActiveLinkId] = React.useState("");

  const scrollToSection = React.useCallback(
    async (linkId: string) => {
      const link = links.find((l) => l.id === linkId);
      if (!link) {
        return;
      }

      let calculatedPath = (link as any).isSection
        ? `${(link as any).page}/${(link as any).section}`
        : `${(link as any).page}`;

      let isPartOfScrollablePages = false;
      for (const page of pagesWithScrolls) {
        if (calculatedPath.includes(page)) {
          isPartOfScrollablePages = true;
          break;
        }
      }
      if (!isPartOfScrollablePages) {
        return;
      }

      for (const page of pagesWithScrolls) {
        calculatedPath = calculatedPath.replaceAll(`/${page}/`, "");
      }

      const targetParts = calculatedPath.split(`c_`);
      let sectionEl: HTMLElement = null as any;
      let sectionElQuery = "";
      for (const targetPart of targetParts) {
        if (!targetPart) {
          continue;
        }
        const needsClick = calculatedPath.includes(`c_${targetPart}`);
        if (needsClick) {
          let el = document.querySelector(`.ant-tabs-tab[data-node-key="${targetPart}"]`) as HTMLElement;
          let tryCount = 0;
          const tryThreshold = 3;
          while (!el && tryCount <= tryThreshold) {
            el = document.querySelector(`.ant-tabs-tab[data-node-key="${targetPart}"]`) as HTMLElement;
            tryCount = tryCount + 1;
            await nextTick();
          }
          if (el) {
            el.click();
          }
          sectionElQuery = `.cpln-form-section[data-section-id="${targetPart}"]`;
          sectionEl = document.querySelector(sectionElQuery) as HTMLElement;
        } else {
          sectionElQuery = `.cpln-form-section[data-section-id="${targetPart}"]`;
          sectionEl = document.querySelector(sectionElQuery) as HTMLElement;
        }
      }

      const container = document.querySelector(`#page main`);
      if (!container) {
        return;
      }

      if (sectionEl && container) {
        const rect = sectionEl.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const margin = 200;
        const targetValue = container.scrollTop + rect.top - containerRect.top - margin;
        container.scrollTo({
          top: targetValue,
          behavior: "smooth",
        });
        isScrollToFinished(sectionElQuery, targetValue);
      }
    },
    [links, pagesWithScrolls],
  );

  function isScrollToFinished(query: string, targetValue: number) {
    const checkIfScrollToIsFinished = setInterval(() => {
      const container = document.querySelector(`#page main`);
      if (!container) {
        return;
      }

      const el = document.querySelector(query);
      if (!el) {
        return;
      }

      const diff = container.scrollTop - targetValue;
      if (diff < 5 && diff > -5) {
        clearInterval(checkIfScrollToIsFinished);
      }
    }, 25);
  }

  return {
    activeLinkId,
    setActiveLinkId,
    scrollToSection,
  };
}

export function useSingleNavMenuContext() {
  return React.useContext(SingleNavMenuContext);
}
